import React, { createContext, useContext, useState, useCallback } from 'react';

interface PersonalInfo {
  name: string;
  title: string;
  department: string;
  email: string;
  phone: string;
  mobile?: string;
  socialLinks: { platform: string; url: string; }[];
  profileImage?: string;
}

interface PersonalInfoContextType {
  personalInfo: PersonalInfo;
  updatePersonalInfo: (newInfo: Partial<PersonalInfo>) => void;
  resetPersonalInfo: () => void;
}

const defaultPersonalInfo: PersonalInfo = {
  name: '',
  title: '',
  department: '',
  email: '',
  phone: '',
  mobile: '',
  socialLinks: [],
  profileImage: ''
};

const PersonalInfoContext = createContext<PersonalInfoContextType | undefined>(undefined);

export const usePersonalInfo = () => {
  const context = useContext(PersonalInfoContext);
  if (!context) {
    throw new Error('usePersonalInfo must be used within a PersonalInfoProvider');
  }
  return context;
};

export const PersonalInfoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [personalInfo, setPersonalInfo] = useState<PersonalInfo>(defaultPersonalInfo);

  const updatePersonalInfo = useCallback((newInfo: Partial<PersonalInfo>) => {
    setPersonalInfo((prev) => ({ ...prev, ...newInfo }));
  }, []);

  const resetPersonalInfo = useCallback(() => {
    setPersonalInfo(defaultPersonalInfo);
  }, []);

  return (
    <PersonalInfoContext.Provider value={{ personalInfo, updatePersonalInfo, resetPersonalInfo }}>
      {children}
    </PersonalInfoContext.Provider>
  );
}; 